import React from 'react';
import { prefetchChunk } from '../../~reusables/util/chunkLoadRecovery';

// Lazy-loaded route components with prefetch support
const lazyWithPrefetch = <T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
) => {
  const Component = React.lazy(importFn);
  // Attach the import function for prefetching
  (Component as unknown as { prefetch: typeof importFn }).prefetch = importFn;
  return Component as React.LazyExoticComponent<T> & {
    prefetch: typeof importFn;
  };
};

export const AppOverview = lazyWithPrefetch(
  () => import('../AppViews/AppOverview'),
);
export const Build = lazyWithPrefetch(() => import('../AppViews/Build'));
export const Builds = lazyWithPrefetch(() => import('../AppViews/Builds'));
export const GeneralSettings = lazyWithPrefetch(() =>
  import('../AppViews/Settings/GeneralSettings').then((m) => ({
    default: m.GeneralSettings,
  })),
);
export const BuildDeploySettings = lazyWithPrefetch(() =>
  import('../AppViews/Settings/BuildDeploySettings').then((m) => ({
    default: m.BuildDeploySettings,
  })),
);
export const CertificatesSettings = lazyWithPrefetch(() =>
  import('../AppViews/Settings/CertificatesSettings').then((m) => ({
    default: m.CertificatesSettings,
  })),
);
export const Authenticate = lazyWithPrefetch(() => import('../Authenticate'));
export const AuthenticateDesktopApp = lazyWithPrefetch(() =>
  import('../Authenticate').then((m) => ({
    default: m.AuthenticateDesktopApp,
  })),
);
export const Settings = lazyWithPrefetch(() => import('../TeamViews/Settings'));
export const ReleaseRouter = lazyWithPrefetch(() =>
  import('../routes/ReleaseRouter').then((m) => ({ default: m.ReleaseRouter })),
);
export const ApplicationList = lazyWithPrefetch(() =>
  import('../TeamViews/ApplicationList').then((m) => ({
    default: m.ApplicationList,
  })),
);
export const DesktopCheckoutRedirect = lazyWithPrefetch(() =>
  import('../DesktopCheckoutRedirect').then((m) => ({
    default: m.DesktopCheckoutRedirect,
  })),
);
export const PulsePage = lazyWithPrefetch(
  () => import('../SuperAdminViews/pulse/PulsePage'),
);
export const InboxPage = lazyWithPrefetch(
  () => import('../SuperAdminViews/inbox/InboxPage'),
);
export const BuildHealthPage = lazyWithPrefetch(
  () => import('../SuperAdminViews/builds/BuildHealthPage'),
);
export const CustomersPage = lazyWithPrefetch(
  () => import('../SuperAdminViews/customers/CustomersPage'),
);
export const CustomerDetailPage = lazyWithPrefetch(
  () => import('../SuperAdminViews/customers/CustomerDetailPage'),
);
export const FeatureFlagsAdmin = lazyWithPrefetch(
  () => import('../SuperAdminViews/FeatureFlagsAdmin'),
);
export const Analytics = lazyWithPrefetch(
  () => import('../AppViews/Analytics'),
);

// Routers for the app and super admin pages, whose layouts only those pages use
export const AppLevelRouter = lazyWithPrefetch(
  () => import('./AppLevelRouter'),
);
export const SuperAdminLevelRouter = lazyWithPrefetch(
  () => import('./SuperAdminLevelRouter'),
);
export const LegacySettingsRouter = lazyWithPrefetch(
  () => import('./LegacySettingsRouter'),
);

// Account-level pages (new design)
export const AccountHomePage = lazyWithPrefetch(() =>
  import('../AccountViews/AccountHomePage').then((m) => ({
    default: m.AccountHomePage,
  })),
);
export const ProfilePage = lazyWithPrefetch(() =>
  import('../AccountViews/ProfilePage').then((m) => ({
    default: m.ProfilePage,
  })),
);
export const TeamGeneralPage = lazyWithPrefetch(() =>
  import('../AccountViews/TeamGeneralPage').then((m) => ({
    default: m.TeamGeneralPage,
  })),
);
export const TransferPage = lazyWithPrefetch(() =>
  import('../AccountViews/TransferPage').then((m) => ({
    default: m.TransferPage,
  })),
);
export const CreateTeamPage = lazyWithPrefetch(() =>
  import('../AccountViews/CreateTeamPage').then((m) => ({
    default: m.CreateTeamPage,
  })),
);
export const InvitePage = lazyWithPrefetch(() =>
  import('../AccountViews/InvitePage').then((m) => ({
    default: m.InvitePage,
  })),
);
export const TeamSetupPage = lazyWithPrefetch(() =>
  import('../AccountViews/TeamSetupPage').then((m) => ({
    default: m.TeamSetupPage,
  })),
);
export const SecurityTokensPage = lazyWithPrefetch(() =>
  import('../AccountViews/SecurityTokensPage').then((m) => ({
    default: m.SecurityTokensPage,
  })),
);
export const AccessTokensPage = lazyWithPrefetch(() =>
  import('../AccountViews/AccessTokensPage').then((m) => ({
    default: m.AccessTokensPage,
  })),
);
export const BillingPage = lazyWithPrefetch(() =>
  import('../AccountViews/BillingPage').then((m) => ({
    default: m.BillingPage,
  })),
);
export const UserManagementPage = lazyWithPrefetch(() =>
  import('../AccountViews/UserManagementPage').then((m) => ({
    default: m.UserManagementPage,
  })),
);
export const SigningPage = lazyWithPrefetch(() =>
  // The route: its own v2 gate, which a team's signing managers pass.
  import('../AccountViews/SigningPage').then((m) => ({
    default: m.SigningRoute,
  })),
);
// ToDesktop v2's pages, in chunks of their own so v1 apps never load them
export const V2AppRouter = lazyWithPrefetch(() =>
  import('../../components/v2/V2AppRouter').then((m) => ({
    default: m.V2AppRouter,
  })),
);
export const V2GitHubCallback = lazyWithPrefetch(() =>
  import('../../components/v2/V2GitHub').then((m) => ({
    default: m.V2GitHubCallback,
  })),
);

/**
 * The route chunks each page renders. The page being opened loads them
 * alongside its data, rather than after the data has loaded.
 */
const pageChunks: [RegExp, { prefetch: () => Promise<unknown> }[]][] = [
  [/^\/apps\/[^/]+\/builds\/[^/]+\/release/, [ReleaseRouter]],
  [/^\/apps\/[^/]+\/builds\/[^/]+/, [AppLevelRouter, Build]],
  [/^\/apps\/[^/]+\/builds\/?$/, [AppLevelRouter, Builds]],
  [/^\/apps\/[^/]+\/analytics/, [AppLevelRouter, Analytics]],
  [
    /^\/apps\/[^/]+\/settings\/build-deploy/,
    [AppLevelRouter, BuildDeploySettings],
  ],
  [
    /^\/apps\/[^/]+\/settings\/certificates/,
    [AppLevelRouter, CertificatesSettings],
  ],
  [/^\/apps\/[^/]+\/settings/, [AppLevelRouter, GeneralSettings]],
  [/^\/apps\/[^/]+/, [AppLevelRouter, AppOverview]],
  [/^\/settings\/profile/, [ProfilePage]],
  [/^\/settings\/security-tokens/, [SecurityTokensPage]],
  [/^\/settings\/access-tokens/, [AccessTokensPage]],
  [/^\/settings\/billing/, [BillingPage]],
  [/^\/settings\/users/, [UserManagementPage]],
  [/^\/settings\/team/, [TeamGeneralPage]],
  [/^\/settings\/?$/, [LegacySettingsRouter, Settings]],
  [/^\/super-admin/, [SuperAdminLevelRouter]],
  [/^\/(login|signup)/, [Authenticate]],
  [/^\/?$/, [AccountHomePage]],
];

export const prefetchPage = (pathname: string) => {
  const chunks = pageChunks.find(([pattern]) => pattern.test(pathname))?.[1];
  chunks?.forEach((chunk) => void prefetchChunk(chunk.prefetch));
};

// Prefetch route chunks in priority order during browser idle time
export const prefetchRoutes = () => {
  // Order by most likely to be visited first
  const routes = [
    AccountHomePage, // Landing page after login (new design)
    ApplicationList, // Landing page after login (legacy)
    AppLevelRouter, // Layout of every app page
    AppOverview, // Most common app view
    Builds, // Builds list
    Build, // Individual build
    GeneralSettings, // App settings - general
    BuildDeploySettings, // App settings - build and deploy
    CertificatesSettings, // App settings - certificates
    ReleaseRouter, // Release flow
    Analytics, // Analytics page
    ProfilePage, // Account profile
    SecurityTokensPage, // Account settings
    AccessTokensPage,
    BillingPage,
    UserManagementPage,
    TeamGeneralPage,
    LegacySettingsRouter, // Legacy team settings
    Settings,
    Authenticate, // Auth pages (lower priority - usually already loaded)
    AuthenticateDesktopApp,
    DesktopCheckoutRedirect,
  ];

  // Load sequentially - each one starts after the previous completes
  const prefetchOne = (route: (typeof routes)[number]) =>
    new Promise<void>((resolve) => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(
          () => {
            void prefetchChunk(route.prefetch).then(resolve);
          },
          { timeout: 5000 },
        );
      } else {
        // Fallback for Safari
        setTimeout(() => {
          void prefetchChunk(route.prefetch).then(resolve);
        }, 100);
      }
    });

  routes.reduce(
    (chain, route) => chain.then(() => prefetchOne(route)),
    Promise.resolve(),
  );
};
